import React, { useEffect, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { Navbar } from "./components/layout/Navbar";
import { MobileNav } from "./components/layout/MobileNav";

// Scroll to top on route change
const ScrollToTop = () => {
  const { pathname } = useLocation();
  
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  
  return null;
};

// Pages
import LandingPage from "./pages/LandingPage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import AboutPage from "./pages/AboutPage";
import EventsPage from "./pages/EventsPage";
import MeetCoachesPage from "./pages/MeetCoachesPage";
import ApplyCoachPage from "./pages/ApplyCoachPage";
import DashboardPage from "./pages/DashboardPage";
import CritiqueRequestPage from "./pages/CritiqueRequestPage";
import CritiqueHistoryPage from "./pages/CritiqueHistoryPage";
import CommunityPage from "./pages/CommunityPage";
import MarketplacePage from "./pages/MarketplacePage";
import ProfilePage from "./pages/ProfilePage";
import CoachDashboardPage from "./pages/CoachDashboardPage";
import AdminPage from "./pages/AdminPage";
import SubscriptionPage from "./pages/SubscriptionPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import LibraryPage from "./pages/LibraryPage";
import BrowsePage from "./pages/BrowsePage";
import CoachProfilePage from "./pages/CoachProfilePage";
import MeetYouthAthletesPage from "./pages/MeetYouthAthletesPage";
import YouthAthleteProfilePage from "./pages/YouthAthleteProfilePage";
import SponsorshipPage from "./pages/SponsorshipPage";
import VideoStudioPage from "./pages/VideoStudioPage";
import VideoResponseViewerPage from "./pages/VideoResponseViewerPage";
import FilmTechniquePage from "./pages/FilmTechniquePage";
import RegistrationPage from "./pages/RegistrationPage";
import StorePage from "./pages/StorePage";
import AgreementsPage from "./pages/AgreementsPage";
import YouthApplicationPage from "./pages/YouthApplicationPage";
import EventGalleryPage from "./pages/EventGalleryPage";
import PurchaseSuccessPage from "./pages/PurchaseSuccessPage";
import VideoOrderPage from "./pages/VideoOrderPage";
import VideoOrderSuccessPage from "./pages/VideoOrderSuccessPage";
import TicketRecoveryPage from "./pages/TicketRecoveryPage";
import TicketSuccessPage from "./pages/TicketSuccessPage";
import SponsorsPage from "./pages/SponsorsPage";
import TVPage from "./pages/TVPage";
import TVPairPage from "./pages/TVPairPage";
import RawUploadPage from "./pages/RawUploadPage";
import DraftHomePage from "./pages/DraftHomePage";
import NetflixLandingPage from "./pages/NetflixLandingPage";
import EventWatchPage from "./pages/EventWatchPage";
import BrandKitPage from "./pages/BrandKitPage";
import BrandBookPage from "./pages/BrandBookPage";

// Helper: determine if the authenticated user has an active/trial subscription
const hasActiveSubscription = (user) => {
  if (!user) return false;
  if (user.subscription_status === 'active') return true;
  if (
    user.subscription_status === 'trial' &&
    user.subscription_end &&
    new Date(user.subscription_end) > new Date()
  ) {
    return true;
  }
  return false;
};

// Protected Route Component
const ProtectedRoute = ({
  children,
  requireCoach = false,
  requireAdmin = false,
  allowStreamManager = false,
  requireSubscription = true, // gate everything behind an active subscription by default
}) => {
  const { isAuthenticated, loading, isCoach, isAdmin, isStreamManager, user } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireCoach && !isCoach) {
    return <Navigate to="/dashboard" replace />;
  }

  if (requireAdmin && !isAdmin) {
    // Allow stream managers to access admin route in stream-manager mode
    if (allowStreamManager && isStreamManager) {
      return children;
    }
    return <Navigate to="/dashboard" replace />;
  }

  // Force non-subscribers to the Netflix home — the lock modal there handles
  // the paywall in-place. Staff roles bypass. (Admins/coaches/stream managers
  // keep their workspaces; everyone else lives on `/`.)
  if (
    requireSubscription &&
    !isAdmin &&
    !isCoach &&
    !isStreamManager &&
    !hasActiveSubscription(user)
  ) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Public Route (redirect to appropriate page if logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isAdmin, isCoach, isStreamManager, loading, user } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="spinner" />
      </div>
    );
  }

  if (isAuthenticated) {
    // Honour a safe in-app ?redirect= (e.g. /browse) before the role defaults.
    const qs = new URLSearchParams(location.search);
    const redirect = qs.get('redirect');
    if (redirect && redirect.startsWith('/') && !redirect.startsWith('//')) {
      const purchase = qs.get('purchase');
      return <Navigate to={purchase ? `${redirect}?purchase=${encodeURIComponent(purchase)}` : redirect} replace />;
    }
    // Only admins get sent to the old admin workspace. Everyone else —
    // coaches, stream managers, subscribers, trial, and brand-new accounts
    // without a plan yet — lands on the Netflix home. The Netflix page's
    // in-place lock modal handles subscribing without ever leaving `/`.
    if (isAdmin) {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/" replace />;
  }

  return children;
};

// Layout with Navbar
import ChatBubble from "./components/ChatBubble";

const AppLayout = ({ children, showNav = true }) => {
  const { isAuthenticated } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  return (
    <div className="min-h-screen bg-background">
      {/* Noise texture overlay */}
      <div className="noise-overlay" />

      {showNav && <Navbar mobileMenuOpen={mobileMenuOpen} setMobileMenuOpen={setMobileMenuOpen} />}
      <main>{children}</main>
      {isAuthenticated && !mobileMenuOpen && <MobileNav />}
      <ChatBubble />
      <Toaster position="top-center" />
    </div>
  );
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<NetflixLandingPage />} />
      <Route
        path="/welcome"
        element={
          <AppLayout>
            <LandingPage />
          </AppLayout>
        }
      />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginPage />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <SignupPage />
          </PublicRoute>
        }
      />
      <Route
        path="/forgot-password"
        element={
          <PublicRoute>
            <ForgotPasswordPage />
          </PublicRoute>
        }
      />
      <Route
        path="/about"
        element={
          <AppLayout>
            <AboutPage />
          </AppLayout>
        }
      />
      <Route
        path="/events"
        element={
          <AppLayout>
            <EventsPage />
          </AppLayout>
        }
      />
      <Route
        path="/gallery"
        element={
          <AppLayout>
            <EventGalleryPage />
          </AppLayout>
        }
      />
      <Route
        path="/gallery/:eventId"
        element={
          <AppLayout>
            <EventGalleryPage />
          </AppLayout>
        }
      />
      <Route
        path="/events/:eventId/order-video"
        element={
          <AppLayout>
            <VideoOrderPage />
          </AppLayout>
        }
      />
      <Route
        path="/purchase/success"
        element={
          <AppLayout>
            <PurchaseSuccessPage />
          </AppLayout>
        }
      />
      <Route
        path="/purchase/cancel"
        element={
          <AppLayout>
            <PaymentCancelPage />
          </AppLayout>
        }
      />
      <Route
        path="/video-order/success"
        element={
          <AppLayout>
            <VideoOrderSuccessPage />
          </AppLayout>
        }
      />
      <Route
        path="/recover-ticket"
        element={
          <AppLayout>
            <TicketRecoveryPage />
          </AppLayout>
        }
      />
      <Route
        path="/sponsors"
        element={
          <AppLayout>
            <SponsorsPage />
          </AppLayout>
        }
      />
      <Route
        path="/ticket-success"
        element={
          <AppLayout>
            <TicketSuccessPage />
          </AppLayout>
        }
      />
      <Route
        path="/video-order/cancel"
        element={
          <AppLayout>
            <PaymentCancelPage />
          </AppLayout>
        }
      />
      <Route
        path="/coaches"
        element={
          <AppLayout>
            <MeetCoachesPage />
          </AppLayout>
        }
      />
      <Route
        path="/coaches/:coachId"
        element={
          <AppLayout>
            <CoachProfilePage />
          </AppLayout>
        }
      />
      <Route
        path="/apply-coach"
        element={
          <AppLayout>
            <ApplyCoachPage />
          </AppLayout>
        }
      />
      <Route
        path="/youth-athletes"
        element={
          <AppLayout>
            <MeetYouthAthletesPage />
          </AppLayout>
        }
      />
      <Route
        path="/youth-athletes/:athleteId"
        element={
          <AppLayout>
            <YouthAthleteProfilePage />
          </AppLayout>
        }
      />
      <Route
        path="/youth-application"
        element={
          <AppLayout>
            <YouthApplicationPage />
          </AppLayout>
        }
      />
      <Route
        path="/sponsorship"
        element={
          <AppLayout>
            <SponsorshipPage />
          </AppLayout>
        }
      />
      <Route
        path="/register/:slug"
        element={
          <AppLayout showNav={false}>
            <RegistrationPage />
          </AppLayout>
        }
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AppLayout>
              <DashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/critique/new"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CritiqueRequestPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/critiques"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CritiqueHistoryPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/critiques/:id"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CritiqueHistoryPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/video-response/:projectId"
        element={
          <ProtectedRoute>
            <AppLayout>
              <VideoResponseViewerPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/community"
        element={
          <ProtectedRoute>
            <AppLayout>
              <CommunityPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/marketplace"
        element={
          <ProtectedRoute>
            <AppLayout>
              <MarketplacePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/store"
        element={
          <ProtectedRoute>
            <AppLayout>
              <StorePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/store/order-complete"
        element={
          <ProtectedRoute>
            <AppLayout>
              <StorePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute requireSubscription={false}>
            <AppLayout>
              <ProfilePage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/agreements"
        element={
          <ProtectedRoute requireSubscription={false}>
            <AppLayout>
              <AgreementsPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/subscription"
        element={
          <ProtectedRoute requireSubscription={false}>
            <AppLayout>
              <SubscriptionPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/success"
        element={
          <ProtectedRoute requireSubscription={false}>
            <AppLayout showNav={false}>
              <PaymentSuccessPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payment/cancel"
        element={
          <ProtectedRoute requireSubscription={false}>
            <AppLayout showNav={false}>
              <PaymentCancelPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/library"
        element={
          <ProtectedRoute>
            <AppLayout>
              <LibraryPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Coach Routes */}
      <Route
        path="/coach"
        element={
          <ProtectedRoute requireCoach>
            <AppLayout>
              <CoachDashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/coach/dashboard"
        element={
          <ProtectedRoute requireCoach>
            <AppLayout>
              <CoachDashboardPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Video Studio Route */}
      <Route
        path="/video-studio/:projectId"
        element={
          <ProtectedRoute requireCoach>
            <VideoStudioPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/video-studio"
        element={
          <ProtectedRoute requireCoach>
            <VideoStudioPage />
          </ProtectedRoute>
        }
      />

      {/* Film Technique - Coach Only */}
      <Route
        path="/film-technique"
        element={
          <ProtectedRoute requireCoach>
            <FilmTechniquePage />
          </ProtectedRoute>
        }
      />

      {/* Admin Routes */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute requireAdmin allowStreamManager>
            <AppLayout>
              <AdminPage />
            </AppLayout>
          </ProtectedRoute>
        }
      />

      {/* Smart TV routes */}
      <Route
        path="/tv"
        element={<TVPage />}
      />
      <Route
        path="/tv/pair"
        element={
          <AppLayout>
            <TVPairPage />
          </AppLayout>
        }
      />

      {/* Camera operator raw upload — public, passcode-gated */}
      <Route path="/raw" element={<RawUploadPage />} />
      <Route path="/raw/*" element={<RawUploadPage />} />
      <Route path="/admin/raw" element={<RawUploadPage />} />

      {/* Draft homepage — internal preview only, not wired into nav */}
      <Route path="/drafthomepage" element={<DraftHomePage />} />
      <Route path="/netflix" element={<Navigate to="/" replace />} />
      <Route path="/browse" element={<BrowsePage />} />
      <Route path="/events/:eventId/watch" element={<EventWatchPage />} />


      {/* Brand kit — public internal-facing pages (no login) */}
      <Route path="/brand-kit" element={<BrandKitPage />} />
      <Route path="/brand-book" element={<BrandBookPage />} />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ScrollToTop />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
